import { useEffect, useState, type FormEvent } from 'react';
import { adminApi, errorText, type WebinarSummary } from './api';
import { navigate } from './app';

const STATUS_LABEL: Record<WebinarSummary['status'], string> = { draft: 'Entwurf', active: 'Aktiv', archived: 'Archiviert' };
const VIDEO_LABEL: Record<string, string> = {
  queued: 'Video wartet',
  probing: 'Video wird geprüft',
  transcoding: 'Video wird aufbereitet',
  ready: 'Video fertig',
  rejected: 'Video abgelehnt',
  failed: 'Video fehlgeschlagen',
};

export function WebinarListPage() {
  const [webinars, setWebinars] = useState<WebinarSummary[] | null>(null);
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    adminApi
      .listWebinars()
      .then((r) => setWebinars(r.webinars))
      .catch(() => setWebinars([]));
  }, []);

  async function create(event: FormEvent) {
    event.preventDefault();
    if (!title.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const { id } = await adminApi.createWebinar(title.trim());
      navigate(`/admin/webinare/${id}`);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="admin-head">
        <h1>Webinare</h1>
      </div>

      <form className="card admin-create" onSubmit={create}>
        <div className="field" style={{ flexGrow: 1 }}>
          <label htmlFor="title">Neues Webinar</label>
          <input id="title" type="text" placeholder="Titel, z. B. Die ersten 100 Kunden" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} />
        </div>
        <button type="submit" className="button" disabled={busy || !title.trim()}>
          Anlegen
        </button>
        {error && <div className="error" style={{ flexBasis: '100%' }}>{error}</div>}
      </form>

      {webinars === null && <p className="muted">Wird geladen …</p>}
      {webinars?.length === 0 && <p className="muted">Noch kein Webinar. Leg oben das erste an.</p>}
      {webinars && webinars.length > 0 && (
        <table className="table">
          <thead>
            <tr>
              <th>Titel</th>
              <th>Status</th>
              <th>Dauer</th>
              <th>Video</th>
              <th>Anmeldungen</th>
              <th>Fragen</th>
            </tr>
          </thead>
          <tbody>
            {webinars.map((w) => (
              <tr key={w.id} className="table__row--link" onClick={() => navigate(`/admin/webinare/${w.id}`)}>
                <td>
                  <a href={`/admin/webinare/${w.id}`} onClick={(e) => e.preventDefault()}>
                    {w.title}
                  </a>
                  <div className="faint small">/{w.slug}</div>
                </td>
                <td>
                  <span className={`tag tag--${w.status}`}>{STATUS_LABEL[w.status]}</span>
                </td>
                <td>{Math.round(w.durationSeconds / 60)} Min.</td>
                <td className={w.video ? '' : 'faint'}>{w.video ? `${VIDEO_LABEL[w.video.status] ?? w.video.status}${w.video.status === 'transcoding' ? ` (${w.video.progressPercent} %)` : ''}` : 'kein Video'}</td>
                <td>{w.registrations}</td>
                <td>{w.openQuestions > 0 ? <span className="tag tag--attention">{w.openQuestions} offen</span> : <span className="faint">–</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}
